import { useCallback } from 'react';
import { useModal } from 'flow/contexts/ModalProvider';
import { useAppDispatch } from 'flow/hooks/useRedux';
import { ticketWriteWasRefused } from 'flow/utils/ticket-utils';

/**
 * Runs a ticket action and tells the user when the backend refuses it.
 *
 * A 403 means the ticket's access level changed under the user: the stored
 * viewerPermissions was resolved when the ticket was last fetched, and nothing is
 * pushed when it changes, so the controls on screen are the ones the user had a
 * moment ago. The dialog says what happened, and the action layer fetches the
 * ticket again so those controls stop being offered.
 *
 * Use this in place of dispatch for every ticket action, so the refusal is
 * recognised in one place rather than in a catch block in each component. The
 * returned promise still rejects — the action did fail, and what follows a
 * success must not run — so callers keep their own then/catch/finally.
 */
const useTicketAction = () => {
    const dispatch = useAppDispatch();
    const { openModal } = useModal();

    return useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (ticketAction: any): Promise<unknown> =>
            dispatch(ticketAction).catch((error?: { status?: number }) => {
                if (ticketWriteWasRefused(error)) {
                    openModal('accessDenied');
                }
                throw error;
            }),
        [dispatch, openModal],
    );
};

export default useTicketAction;
